import axios, { AxiosError } from 'axios';
import { getClientEnv } from './clientEnv';

declare global {
  interface Window {
    localStorage: Storage;
  }
}

// Determinar la URL base según el entorno
const { mode, apiUrl } = getClientEnv();
const isDevelopment = mode === 'development';

// En producción, usar la URL de la API de producción o la URL actual si está desplegada en Vercel
const API_URL = isDevelopment 
  ? 'http://localhost:3000' 
  : (apiUrl || window.location.origin);

console.log('API URL:', API_URL);
console.log('Environment:', mode);

// Configuración base de axios
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  timeout: 15000,
  withCredentials: false
});

// Interceptor para añadir el token a las peticiones
axiosInstance.interceptors.request.use(
  (config) => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Log de la petición en desarrollo
      if (isDevelopment) {
        console.log(`[${config.method?.toUpperCase()}] ${config.url}`, config);
      }
      
      return config;
    } catch (error) {
      console.error('[axios] Error en la configuración de la petición:', error);
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores y transformar respuestas
axiosInstance.interceptors.response.use(
  (response) => {
    // Log de la respuesta en desarrollo
    if (isDevelopment) {
      console.log(`[Response] ${response.config.url}:`, response.data);
    }
    
    // Transformar las URLs de las imágenes en las respuestas
    if (response.data && Array.isArray(response.data)) {
      response.data = response.data.map(item => {
        if (item.images && Array.isArray(item.images)) {
          item.images = item.images.map((image: string) => {
            if (image.startsWith('http://') || image.startsWith('https://')) {
              return image;
            }
            if (image.startsWith('/')) {
              return `${API_URL}${image}`;
            }
            if (image.match(/^[0-9a-fA-F]{24}$/)) {
              return `${API_URL}/api/images/${image}`;
            }
            return image;
          });
        }
        return item;
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    // Log del error en desarrollo
    if (isDevelopment) {
      console.error('[axios] Error:', {
        status: error.response?.status,
        data: error.response?.data,
        config: error.config
      });
    }
    
    if (error.response) {
      // Error de autenticación
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      
      // Error de servidor
      if (error.response.status >= 500) {
        console.error('[axios] Error del servidor:', error.response.data);
      }
      
      throw error;
    } else if (error.request) {
      console.error('[axios] Error de red:', error.message);
      throw new Error('Error de conexión. Por favor, verifica tu conexión a internet.');
    } else {
      console.error('[axios] Error de configuración:', error.message);
      throw error;
    }
  }
);

export default axiosInstance; 